// CameraSettingsPage - 2ECOMP Fleet Management
// Camera configuration and settings management

import React, {
 useState, useCallback, useEffect, useMemo 
} from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  CardHeader,
  Select,
  MenuItem,
  FormControl,
  FormControlLabel,
  InputLabel,
  Switch,
  Button,
  IconButton,
  Divider,
  Stack,
  Chip,
  Alert,
  AlertTitle,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Tabs,
  Tab,
  CircularProgress,
  Tooltip,
  Slider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
} from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import RefreshIcon from '@mui/icons-material/Refresh';
import VideocamIcon from '@mui/icons-material/Videocam';
import StorageIcon from '@mui/icons-material/Storage';
import NetworkCheckIcon from '@mui/icons-material/NetworkCheck';
import NotificationsIcon from '@mui/icons-material/Notifications';
import TuneIcon from '@mui/icons-material/Tune';
import InfoIcon from '@mui/icons-material/Info';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import WarningIcon from '@mui/icons-material/Warning';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';

import { useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  STREAM_PROTOCOLS,
  STREAM_QUALITY,
  VIDEO_TRIGGER_EVENTS,
  RECORDING_DEFAULTS,
  PLAYER_SETTINGS,
  CHANNEL_NAMES,
} from '../utils/cameraConstants';
import { getJt1078Config } from '../utils/streamUrls';
import { useDeviceCameraStatus } from '../hooks/useCameraStatus';
import { useCameraServer } from '../hooks/useCameraServer';

// Styled Components
const PageContainer = styled(Box)(({ theme }) => ({
  minHeight: '100dvh',
  backgroundColor: theme.palette.background.default,
  paddingBottom: 'env(safe-area-inset-bottom, 0px)',
}));

const Header = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(1.5, 2),
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  flexWrap: 'wrap',
  gap: theme.spacing(1),
  borderRadius: 0,
  position: 'sticky',
  top: 0,
  zIndex: theme.zIndex.appBar,
  borderBottom: `1px solid ${theme.palette.divider}`,
  [theme.breakpoints.up('md')]: {
    flexWrap: 'nowrap',
  },
}));

const ContentContainer = styled(Container)(({ theme }) => ({
  paddingTop: theme.spacing(3),
  paddingBottom: theme.spacing(3),
}));

const SettingsCard = styled(Card)(({ theme }) => ({
  height: '100%',
}));

const StatusIndicator = styled(Box)(({ theme, status }) => ({
  width: 10,
  height: 10,
  borderRadius: '50%',
  backgroundColor:
    status === 'online' ? theme.palette.success.main :
    status === 'offline' ? theme.palette.error.main :
    theme.palette.warning.main,
}));

const TabPanel = ({ children, value, index, ...other }) => (
  <Box
    role="tabpanel"
    hidden={value !== index}
    id={`settings-tabpanel-${index}`}
    {...other}
    sx={{ py: 3 }}
  >
    {value === index && children}
  </Box>
);

/**
 * CameraSettingsPage - Camera configuration interface
 */
const CameraSettingsPage = () => {
  const navigate = useNavigate();
  const { deviceId } = useParams();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Redux state
  const devicesMap = useSelector((state) => state.devices.items);
  const selectedDevice = useMemo(() =>
    Object.values(devicesMap || {}).find(d => String(d.id) === deviceId),
    [devicesMap, deviceId]
  );

  // Camera server config (from backend /api/server)
  const cameraServer = useCameraServer();

  // Camera status
  const { activeChannels, hasCamera, refresh: refreshStatus } = useDeviceCameraStatus(deviceId);

  // Local state
  const [activeTab, setActiveTab] = useState(0);
  const [isSaving, setIsSaving] = useState(false);
  const [settings, setSettings] = useState({
    // Stream Settings
    preferredProtocol: STREAM_PROTOCOLS.WEBRTC,
    streamQuality: STREAM_QUALITY.MAIN,
    autoReconnect: true,
    reconnectAttempts: PLAYER_SETTINGS.reconnectAttempts,
    reconnectDelay: PLAYER_SETTINGS.reconnectDelay / 1000,
    bufferDuration: PLAYER_SETTINGS.bufferDuration,
    lowLatencyMode: PLAYER_SETTINGS.lowLatencyMode,

    // Recording Settings
    enableRecording: true,
    preEventSeconds: RECORDING_DEFAULTS.preEventSeconds,
    postEventSeconds: RECORDING_DEFAULTS.postEventSeconds,
    maxClipDuration: RECORDING_DEFAULTS.maxClipDuration,
    recordingEvents: VIDEO_TRIGGER_EVENTS.slice(0, 5),

    // Notification Settings
    notifyOnStreamStart: false,
    notifyOnStreamEnd: false,
    notifyOnRecording: true,

    // Channel Settings
    enabledChannels: [1, 2, 3, 4],
    defaultChannel: 1,
  });

  // JT1078 Server Config
  const jt1078Config = getJt1078Config(cameraServer);

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  // Handle setting change
  const handleSettingChange = useCallback((key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  }, []);

  // Handle save
  const handleSave = useCallback(async () => {
    setIsSaving(true);
    try {
      // Here you would save to backend
      // await api.saveCameraSettings(deviceId, settings);
      await new Promise(resolve => setTimeout(resolve, 1000)); // Simulate
    } finally {
      setIsSaving(false);
    }
  }, [settings, deviceId]);

  // Server status check
  const [serverStatus, setServerStatus] = useState({
    m7s: 'checking',
    jt1078: 'checking',
  });

  useEffect(() => {
    // Check server connectivity
    const checkServers = async () => {
      try {
        await fetch(`${cameraServer.baseUrl}/`, { mode: 'no-cors' });
        setServerStatus(prev => ({ ...prev, m7s: 'online' }));
      } catch {
        setServerStatus(prev => ({ ...prev, m7s: 'offline' }));
      }

      // JT1078 would need a different check
      setServerStatus(prev => ({ ...prev, jt1078: 'online' }));
    };

    checkServers();
  }, [cameraServer.baseUrl]);

  return (
    <PageContainer>
      {/* Header */}
      <Header elevation={2}>
        <Stack direction="row" spacing={2} alignItems="center">
          <IconButton onClick={() => navigate(-1)}>
            <ArrowBackIcon />
          </IconButton>
          <Box>
            <Typography variant="h6">
              Configurações de Câmera
            </Typography>
            {selectedDevice && (
              <Typography variant="caption" color="text.secondary">
                {selectedDevice.name}
              </Typography>
            )}
          </Box>
        </Stack>

        <Stack direction="row" spacing={1}>
          {isMobile ? (
            <>
              <Tooltip title="Atualizar Status">
                <IconButton onClick={refreshStatus}>
                  <RefreshIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Salvar">
                <IconButton color="primary" onClick={handleSave} disabled={isSaving}>
                  {isSaving ? <CircularProgress size={20} /> : <SaveIcon />}
                </IconButton>
              </Tooltip>
            </>
          ) : (
            <>
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={refreshStatus}
              >
                Atualizar Status
              </Button>
              <Button
                variant="contained"
                startIcon={isSaving ? <CircularProgress size={16} /> : <SaveIcon />}
                onClick={handleSave}
                disabled={isSaving}
              >
                Salvar
              </Button>
            </>
          )}
        </Stack>
      </Header>

      <ContentContainer maxWidth="lg">
        {/* Server Status Alert */}
        <Alert
          severity={serverStatus.m7s === 'online' ? 'success' : 'warning'}
          sx={{ mb: 3 }}
          icon={serverStatus.m7s === 'online' ? <CheckCircleIcon /> : <WarningIcon />}
        >
          <AlertTitle>Status do Servidor de Mídia</AlertTitle>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 1, sm: 3 }}>
            <Stack direction="row" spacing={1} alignItems="center">
              <StatusIndicator status={serverStatus.m7s} />
              <Typography variant="body2">
                M7S/Monibuca: {serverStatus.m7s === 'online' ? 'Conectado' : 'Verificando...'}
              </Typography>
            </Stack>
            <Stack direction="row" spacing={1} alignItems="center">
              <StatusIndicator status={serverStatus.jt1078} />
              <Typography variant="body2">
                JT1078: {jt1078Config.ip}:{jt1078Config.port}
              </Typography>
            </Stack>
          </Stack>
        </Alert>

        {/* Device Camera Status */}
        {selectedDevice && (
          <Paper sx={{ p: 2, mb: 3 }}>
            <Grid container spacing={3} alignItems="center">
              <Grid size={{ xs: 12, md: 4 }}>
                <Stack direction="row" spacing={2} alignItems="center">
                  <VideocamIcon
                    sx={{
                      fontSize: 40,
                      color: hasCamera ? 'success.main' : 'text.disabled',
                    }}
                  />
                  <Box>
                    <Typography variant="subtitle1" fontWeight={600}>
                      {selectedDevice.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      ID: {selectedDevice.uniqueId}
                    </Typography>
                  </Box>
                </Stack>
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <Typography variant="caption" color="text.secondary" display="block">
                  Canais Ativos
                </Typography>
                <Stack direction="row" spacing={0.5} sx={{ mt: 0.5 }}>
                  {[1, 2, 3, 4, 5, 6, 7, 8].map(ch => (
                    <Chip
                      key={ch}
                      label={ch}
                      size="small"
                      color={activeChannels.includes(ch) ? 'success' : 'default'}
                      variant={activeChannels.includes(ch) ? 'filled' : 'outlined'}
                    />
                  ))}
                </Stack>
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <Typography variant="caption" color="text.secondary" display="block">
                  Status
                </Typography>
                <Chip
                  icon={hasCamera ? <CheckCircleIcon /> : <ErrorIcon />}
                  label={hasCamera ? `${activeChannels.length} câmera(s) ao vivo` : 'Offline'}
                  color={hasCamera ? 'success' : 'default'}
                  sx={{ mt: 0.5 }}
                />
              </Grid>
            </Grid>
          </Paper>
        )}

        {/* Settings Tabs */}
        <Paper sx={{ mb: 3 }}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
          >
            <Tab icon={<TuneIcon />} label="Streaming" />
            <Tab icon={<StorageIcon />} label="Gravação" />
            <Tab icon={<NotificationsIcon />} label="Notificações" />
            <Tab icon={<NetworkCheckIcon />} label="Servidor" />
            <Tab icon={<InfoIcon />} label="Informações" />
          </Tabs>
        </Paper>

        {/* Tab: Streaming */}
        <TabPanel value={activeTab} index={0}>
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, md: 6 }}>
              <SettingsCard>
                <CardHeader
                  title="Protocolo de Streaming"
                  subheader="Configure o protocolo preferido para transmissão"
                />
                <CardContent>
                  <FormControl fullWidth sx={{ mb: 3 }}>
                    <InputLabel>Protocolo Preferido</InputLabel>
                    <Select
                      value={settings.preferredProtocol}
                      label="Protocolo Preferido"
                      onChange={(e) => handleSettingChange('preferredProtocol', e.target.value)}
                    >
                      <MenuItem value={STREAM_PROTOCOLS.WEBRTC}>
                        WebRTC (Menor Latência)
                      </MenuItem>
                      <MenuItem value={STREAM_PROTOCOLS.WS_FLV}>
                        WebSocket FLV (Boa Latência)
                      </MenuItem>
                      <MenuItem value={STREAM_PROTOCOLS.FLV}>
                        HTTP FLV (Compatível)
                      </MenuItem>
                      <MenuItem value={STREAM_PROTOCOLS.HLS}>
                        HLS (Alta Compatibilidade)
                      </MenuItem>
                    </Select>
                  </FormControl>

                  <FormControl fullWidth sx={{ mb: 3 }}>
                    <InputLabel>Qualidade do Stream</InputLabel>
                    <Select
                      value={settings.streamQuality}
                      label="Qualidade do Stream"
                      onChange={(e) => handleSettingChange('streamQuality', e.target.value)}
                    >
                      <MenuItem value={STREAM_QUALITY.MAIN}>Principal (HD)</MenuItem>
                      <MenuItem value={STREAM_QUALITY.SUB}>Secundário (SD)</MenuItem>
                      <MenuItem value={STREAM_QUALITY.AUTO}>Automático</MenuItem>
                    </Select>
                  </FormControl>

                  <FormControlLabel
                    control={
                      <Switch
                        checked={settings.lowLatencyMode}
                        onChange={(e) => handleSettingChange('lowLatencyMode', e.target.checked)}
                      />
                    }
                    label="Modo Baixa Latência"
                  />
                </CardContent>
              </SettingsCard>
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <SettingsCard>
                <CardHeader
                  title="Reconexão Automática"
                  subheader="Configure o comportamento de reconexão"
                />
                <CardContent>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={settings.autoReconnect}
                        onChange={(e) => handleSettingChange('autoReconnect', e.target.checked)}
                      />
                    }
                    label="Reconectar Automaticamente"
                    sx={{ mb: 3 }}
                  />

                  <Typography gutterBottom>
                    Tentativas de Reconexão: {settings.reconnectAttempts}
                  </Typography>
                  <Slider
                    value={settings.reconnectAttempts}
                    onChange={(e, v) => handleSettingChange('reconnectAttempts', v)}
                    min={1}
                    max={10}
                    marks
                    disabled={!settings.autoReconnect}
                    sx={{ mb: 3 }}
                  />

                  <Typography gutterBottom>
                    Intervalo (segundos): {settings.reconnectDelay}
                  </Typography>
                  <Slider
                    value={settings.reconnectDelay}
                    onChange={(e, v) => handleSettingChange('reconnectDelay', v)}
                    min={1}
                    max={10}
                    marks
                    disabled={!settings.autoReconnect}
                  />
                </CardContent>
              </SettingsCard>
            </Grid>

            <Grid size={12}>
              <SettingsCard>
                <CardHeader
                  title="Canais Habilitados"
                  subheader="Selecione os canais de câmera para monitorar"
                />
                <CardContent>
                  <Grid container spacing={2}>
                    {Object.entries(CHANNEL_NAMES).map(([ch, name]) => (
                      <Grid size={{ xs: 6, sm: 3 }} key={ch}>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={settings.enabledChannels.includes(parseInt(ch))}
                              onChange={(e) => {
                                const channel = parseInt(ch);
                                const newChannels = e.target.checked
                                  ? [...settings.enabledChannels, channel]
                                  : settings.enabledChannels.filter(c => c !== channel);
                                handleSettingChange('enabledChannels', newChannels);
                              }}
                            />
                          }
                          label={`${ch}: ${name}`}
                        />
                      </Grid>
                    ))}
                  </Grid>
                </CardContent>
              </SettingsCard>
            </Grid>
          </Grid>
        </TabPanel>

        {/* Tab: Recording */}
        <TabPanel value={activeTab} index={1}>
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, md: 6 }}>
              <SettingsCard>
                <CardHeader
                  title="Configurações de Gravação"
                  subheader="Configure a gravação de eventos"
                />
                <CardContent>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={settings.enableRecording}
                        onChange={(e) => handleSettingChange('enableRecording', e.target.checked)}
                      />
                    }
                    label="Habilitar Gravação de Eventos"
                    sx={{ mb: 3 }}
                  />

                  <Typography gutterBottom>
                    Segundos antes do evento: {settings.preEventSeconds}
                  </Typography>
                  <Slider
                    value={settings.preEventSeconds}
                    onChange={(e, v) => handleSettingChange('preEventSeconds', v)}
                    min={5}
                    max={30}
                    marks
                    disabled={!settings.enableRecording}
                    sx={{ mb: 3 }}
                  />

                  <Typography gutterBottom>
                    Segundos após o evento: {settings.postEventSeconds}
                  </Typography>
                  <Slider
                    value={settings.postEventSeconds}
                    onChange={(e, v) => handleSettingChange('postEventSeconds', v)}
                    min={5}
                    max={60}
                    marks
                    disabled={!settings.enableRecording}
                    sx={{ mb: 3 }}
                  />

                  <Typography gutterBottom>
                    Duração máxima do clipe: {settings.maxClipDuration}s
                  </Typography>
                  <Slider
                    value={settings.maxClipDuration}
                    onChange={(e, v) => handleSettingChange('maxClipDuration', v)}
                    min={30}
                    max={180}
                    step={30}
                    marks
                    disabled={!settings.enableRecording}
                  />
                </CardContent>
              </SettingsCard>
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <SettingsCard>
                <CardHeader
                  title="Eventos que Acionam Gravação"
                  subheader="Selecione os tipos de eventos"
                />
                <CardContent>
                  <List dense>
                    {VIDEO_TRIGGER_EVENTS.map((event) => (
                      <ListItem key={event} disablePadding>
                        <FormControlLabel
                          control={
                            <Switch
                              size="small"
                              checked={settings.recordingEvents.includes(event)}
                              onChange={(e) => {
                                const newEvents = e.target.checked
                                  ? [...settings.recordingEvents, event]
                                  : settings.recordingEvents.filter(ev => ev !== event);
                                handleSettingChange('recordingEvents', newEvents);
                              }}
                              disabled={!settings.enableRecording}
                            />
                          }
                          label={
                            <Typography variant="body2">
                              {event.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                            </Typography>
                          }
                          sx={{ width: '100%', m: 0 }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </CardContent>
              </SettingsCard>
            </Grid>
          </Grid>
        </TabPanel>

        {/* Tab: Notifications */}
        <TabPanel value={activeTab} index={2}>
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, md: 6 }}>
              <SettingsCard>
                <CardHeader
                  title="Notificações de Câmera"
                  subheader="Configure alertas e notificações"
                />
                <CardContent>
                  <List>
                    <ListItem>
                      <ListItemText
                        primary="Notificar quando stream iniciar"
                        secondary="Receba alerta quando uma câmera começar a transmitir"
                      />
                      <ListItemSecondaryAction>
                        <Switch
                          checked={settings.notifyOnStreamStart}
                          onChange={(e) => handleSettingChange('notifyOnStreamStart', e.target.checked)}
                        />
                      </ListItemSecondaryAction>
                    </ListItem>
                    <Divider />
                    <ListItem>
                      <ListItemText
                        primary="Notificar quando stream terminar"
                        secondary="Receba alerta quando uma câmera parar de transmitir"
                      />
                      <ListItemSecondaryAction>
                        <Switch
                          checked={settings.notifyOnStreamEnd}
                          onChange={(e) => handleSettingChange('notifyOnStreamEnd', e.target.checked)}
                        />
                      </ListItemSecondaryAction>
                    </ListItem>
                    <Divider />
                    <ListItem>
                      <ListItemText
                        primary="Notificar sobre gravações"
                        secondary="Receba alerta quando um evento for gravado"
                      />
                      <ListItemSecondaryAction>
                        <Switch
                          checked={settings.notifyOnRecording}
                          onChange={(e) => handleSettingChange('notifyOnRecording', e.target.checked)}
                        />
                      </ListItemSecondaryAction>
                    </ListItem>
                  </List>
                </CardContent>
              </SettingsCard>
            </Grid>
          </Grid>
        </TabPanel>

        {/* Tab: Server */}
        <TabPanel value={activeTab} index={3}>
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, md: 6 }}>
              <SettingsCard>
                <CardHeader
                  title="Servidor de Mídia (M7S/Monibuca)"
                  subheader="Configurações do servidor de streaming"
                />
                <CardContent>
                  <TableContainer>
                    <Table size="small">
                      <TableBody>
                        <TableRow>
                          <TableCell>Host</TableCell>
                          <TableCell>{cameraServer.host}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>Protocolo</TableCell>
                          <TableCell>{cameraServer.protocol.toUpperCase()}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>Porta HTTP</TableCell>
                          <TableCell>{cameraServer.httpPort}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>Status</TableCell>
                          <TableCell>
                            <Stack direction="row" spacing={1} alignItems="center">
                              <StatusIndicator status={serverStatus.m7s} />
                              <Typography variant="body2">
                                {serverStatus.m7s === 'online' ? 'Conectado' :
                                 serverStatus.m7s === 'checking' ? 'Verificando...' : 'Desconectado'}
                              </Typography>
                            </Stack>
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </SettingsCard>
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <SettingsCard>
                <CardHeader
                  title="Servidor JT1078"
                  subheader="Servidor de protocolo de vídeo"
                />
                <CardContent>
                  <TableContainer>
                    <Table size="small">
                      <TableBody>
                        <TableRow>
                          <TableCell>IP</TableCell>
                          <TableCell>{jt1078Config.ip}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>Porta Stream</TableCell>
                          <TableCell>{jt1078Config.port}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>Porta Playback</TableCell>
                          <TableCell>{jt1078Config.playbackPort}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>Status</TableCell>
                          <TableCell>
                            <Stack direction="row" spacing={1} alignItems="center">
                              <StatusIndicator status={serverStatus.jt1078} />
                              <Typography variant="body2">
                                {serverStatus.jt1078 === 'online' ? 'Ativo' : 'Verificando...'}
                              </Typography>
                            </Stack>
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </SettingsCard>
            </Grid>
          </Grid>
        </TabPanel>

        {/* Tab: Information */}
        <TabPanel value={activeTab} index={4}>
          <Grid container spacing={3}>
            <Grid size={12}>
              <Alert severity="info">
                <AlertTitle>Sobre o Sistema de Câmeras</AlertTitle>
                <Typography variant="body2" paragraph>
                  O sistema de câmeras utiliza o protocolo JT1078 para comunicação com dispositivos
                  DVR veiculares e o servidor Monibuca (M7S) para processamento e distribuição de streams.
                </Typography>
                <Typography variant="body2">
                  <strong>Protocolos suportados:</strong>
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemIcon><CheckCircleIcon color="success" fontSize="small" /></ListItemIcon>
                    <ListItemText
                      primary="WebRTC"
                      secondary="Menor latência (~500ms), ideal para monitoramento em tempo real"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon><CheckCircleIcon color="success" fontSize="small" /></ListItemIcon>
                    <ListItemText
                      primary="FLV (HTTP/WebSocket)"
                      secondary="Boa latência (1-3s), compatível com a maioria dos navegadores"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon><CheckCircleIcon color="success" fontSize="small" /></ListItemIcon>
                    <ListItemText
                      primary="HLS"
                      secondary="Alta compatibilidade (5-10s latência), funciona em iOS/Safari"
                    />
                  </ListItem>
                </List>
              </Alert>
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <SettingsCard>
                <CardHeader title="Versões" />
                <CardContent>
                  <TableContainer>
                    <Table size="small">
                      <TableBody>
                        <TableRow>
                          <TableCell>Módulo de Câmera</TableCell>
                          <TableCell>1.0.0</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>mpegts.js</TableCell>
                          <TableCell>HTTP-FLV player</TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </SettingsCard>
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <SettingsCard>
                <CardHeader title="Documentação" />
                <CardContent>
                  <List dense>
                    <ListItem>
                      <ListItemIcon><HelpOutlineIcon /></ListItemIcon>
                      <ListItemText primary="Guia de Configuração de Câmeras" />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon><HelpOutlineIcon /></ListItemIcon>
                      <ListItemText primary="Protocolo JT1078" />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon><HelpOutlineIcon /></ListItemIcon>
                      <ListItemText primary="Solução de Problemas" />
                    </ListItem>
                  </List>
                </CardContent>
              </SettingsCard>
            </Grid>
          </Grid>
        </TabPanel>
      </ContentContainer>
    </PageContainer>
  );
};

export default CameraSettingsPage;
